import { CONFIG } from "../config";

/**
 * Meta (pixel) dans le navigateur — T43, 04/10/2026.
 *
 * ## Ce que ce module garantit
 *
 * - **Rien sans consentement.** Tant que la personne n'a pas accepté le bandeau,
 *   aucun script de Meta n'est chargé, aucun cookie `_fbp` n'est posé, aucun
 *   appel ne part. Le refus est retenu au même titre que l'accord.
 * - **Rien sans configuration.** Sans identifiant de pixel (`VITE_META_PIXEL_ID`)
 *   ou si l'exploitation a coupé Meta depuis `/pilotage` (`metaActif`), le
 *   module ne fait rien et le bandeau ne s'affiche pas.
 * - **Pas d'achat depuis le navigateur.** L'achat est signalé par le serveur,
 *   sur la seule vente confirmée par l'opérateur (règle n° 3). Ce module ne
 *   connaît que la visite, le diagnostic terminé et l'ouverture du paiement.
 * - **Rien de ce que la personne a déclaré.** Ni métier, ni tâche, ni score ne
 *   partent chez Meta : seulement le nom de l'évènement et, pour l'ouverture du
 *   paiement, le montant du plan.
 *
 * ## Pourquoi pas le snippet de Meta
 *
 * Le snippet officiel est un script en ligne. La politique de sécurité du site
 * refuse tout script en ligne (`script-src 'self'`) — c'est précisément ce
 * qu'elle existe pour arrêter. L'amorce `fbq` est donc réécrite ici, dans le
 * paquet du site, et seul `fbevents.js` est chargé depuis le domaine de Meta.
 */

const CLE_CONSENTEMENT = "stratera.meta.consentement";
const CLE_ENGAGE = "stratera.meta.engage";

/**
 * Le bandeau attend que le diagnostic soit lancé — 06/10/2026.
 *
 * Posé dès l'arrivée, il couvrait sur téléphone le champ métier de l'accueil
 * et la première question de `/dirigeant` : le visiteur venu d'une annonce
 * devait répondre à Meta avant de pouvoir commencer ce pour quoi il était
 * venu. Il paraissait alors au premier geste du parcours.
 *
 * **Révisé le 07/10/2026 : il attend le résultat.** Au premier geste, il
 * tombait sur l'écran des tâches et couvrait les boutons de la première —
 * l'écran précis où il faut agir. Ce jour-là, sept diagnostics lancés sur
 * neuf s'arrêtaient avant l'indice. Il paraît désormais quand l'indice ou le
 * résultat dirigeant est affiché : il n'y a plus rien à remplir, et la
 * personne a reçu ce pour quoi elle est venue. Moins de visiteurs le voient ;
 * c'est le prix, et il est moindre qu'un diagnostic interrompu. Rien ne change
 * à la règle : sans accord, rien ne part.
 *
 * `e2e/rienNeMasque.spec.ts` tient la règle générale : sur téléphone, aucune
 * surcouche ne recouvre un bouton qu'il faut toucher pour avancer.
 */
export const EVENEMENT_ENGAGE = "stratera:parcours-engage";

export function parcoursEngage(): boolean {
  return lire(CLE_ENGAGE, () => sessionStorage) === "1";
}

export function marquerParcoursEngage(): void {
  if (parcoursEngage()) return;
  ecrire(CLE_ENGAGE, "1", () => sessionStorage);
  try {
    window.dispatchEvent(new Event(EVENEMENT_ENGAGE));
  } catch {
    // Hors navigateur : rien à prévenir.
  }
}
const CLE_FBCLID = "stratera.meta.fbc";

export type ChoixMeta = "accepte" | "refuse";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
  push: Fbq;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let actifCote = true;
let charge = false;

/** L'exploitation peut couper Meta sans déploiement (`/api/reglages`). */
export function definirMetaActif(actif: boolean): void {
  actifCote = actif;
}

/** Le pixel est-il configuré et autorisé par l'exploitation ? */
export function metaDisponible(): boolean {
  return Boolean(CONFIG.metaPixelId) && actifCote;
}

function lire(cle: string, stockage: () => Storage): string | null {
  try {
    return stockage().getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string, stockage: () => Storage): void {
  try {
    stockage().setItem(cle, valeur);
  } catch {
    // Navigation privée stricte : le choix vaut pour la page, c'est tout.
  }
}

export function choixMeta(): ChoixMeta | null {
  const v = lire(CLE_CONSENTEMENT, () => localStorage);
  return v === "accepte" || v === "refuse" ? v : null;
}

export function consentementMeta(): boolean {
  return metaDisponible() && choixMeta() === "accepte";
}

/** Retient le choix ; un accord charge le pixel aussitôt. */
export function enregistrerChoixMeta(choix: ChoixMeta): void {
  ecrire(CLE_CONSENTEMENT, choix, () => localStorage);
  if (choix === "accepte") chargerPixel();
}

/**
 * L'identifiant de clic d'une annonce Meta, au format `fbc` que l'API
 * Conversions attend (`fb.1.<horodatage>.<fbclid>`). Lu dans l'adresse
 * d'arrivée, gardé pour l'onglet — comme le marquage de campagne.
 */
export function fbcDepuisUrl(recherche: string, maintenant = Date.now()): string | null {
  const fbclid = new URLSearchParams(recherche).get("fbclid");
  if (!fbclid || !/^[A-Za-z0-9_-]{8,500}$/.test(fbclid)) return null;
  return `fb.1.${maintenant}.${fbclid}`;
}

export function memoriserFbclid(recherche: string = window.location.search): void {
  const fbc = fbcDepuisUrl(recherche);
  if (fbc) ecrire(CLE_FBCLID, fbc, () => sessionStorage);
}

/** Lit un cookie posé par le pixel (`_fbp`, `_fbc`). */
function cookie(nom: string): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${nom}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Ce que l'appel de paiement transmet au serveur pour l'achat — rien sans
 * consentement. Le serveur y ajoute l'adresse IP et le navigateur de la requête.
 */
export function contexteMetaPaiement(): { consentement: true; fbc?: string; fbp?: string } | undefined {
  if (!consentementMeta()) return undefined;
  const fbc = cookie("_fbc") ?? lire(CLE_FBCLID, () => sessionStorage) ?? undefined;
  const fbp = cookie("_fbp") ?? undefined;
  return { consentement: true, ...(fbc ? { fbc } : {}), ...(fbp ? { fbp } : {}) };
}

/** L'amorce `fbq`, telle que le snippet de Meta la pose — sans script en ligne. */
function amorce(): Fbq {
  const f = function (...args: unknown[]) {
    if (f.callMethod) f.callMethod(...args);
    else f.queue.push(args);
  } as Fbq;
  f.push = f;
  f.loaded = true;
  f.version = "2.0";
  f.queue = [];
  return f;
}

export function chargerPixel(): void {
  if (charge || !consentementMeta() || typeof document === "undefined") return;
  charge = true;
  if (!window.fbq) {
    const f = amorce();
    window.fbq = f;
    window._fbq = f;
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }
  window.fbq("init", CONFIG.metaPixelId);
  window.fbq("track", "PageView");
}

/**
 * Les évènements du produit qui intéressent l'optimisation des annonces.
 * Le reste ne part pas. La vue de page part une fois, au chargement du pixel :
 * relayer aussi « view » la compterait deux fois à l'arrivée.
 */
export function evenementMeta(
  nom: string,
  props: Record<string, unknown>,
): { evenement: string; donnees?: Record<string, unknown> } | null {
  switch (nom) {
    case "score_shown":
    case "dirigeant_resultat":
      return { evenement: "Lead" };
    case "pay_open": {
      const montant = typeof props.amount === "number" ? props.amount : undefined;
      return {
        evenement: "InitiateCheckout",
        donnees: montant ? { value: montant, currency: "XOF" } : undefined,
      };
    }
    default:
      return null;
  }
}

/** Relaye un évènement du produit vers le pixel, s'il est chargé et consenti. */
export function relayerMeta(nom: string, props: Record<string, unknown>): void {
  if (!consentementMeta() || !window.fbq) return;
  const e = evenementMeta(nom, props);
  if (!e) return;
  try {
    window.fbq("track", e.evenement, e.donnees ?? {});
  } catch {
    // La mesure publicitaire ne doit jamais casser le parcours.
  }
}
